import React, { useState, useEffect } from 'react';
import {
  Settings,
  Mail,
  Save,
  Plus,
  Edit3,
  Users,
  Building,
  CheckCircle,
  AlertTriangle,
  FileText,
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { CertificationNotificationConfig, CertificationNotificationLog } from '../types';
import certificationNotificationApi from '../services/certificationNotificationApi';
import { ORGANIZATION } from '../config/organization';

const CertificationNotificationAdmin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'configurations' | 'logs' | 'expired'>('configurations');
  const [configs, setConfigs] = useState<CertificationNotificationConfig[]>([]);
  const [logs, setLogs] = useState<CertificationNotificationLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingConfig, setEditingConfig] = useState<CertificationNotificationConfig | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [complianceStats, setComplianceStats] = useState({
    branchCompliance: [] as Array<{
      branch: string;
      total_technicians: number;
      compliant_technicians: number;
      compliance_percentage: number;
      expired_count: number;
      expiring_soon: number;
    }>,
    expiredCertifications: [] as Array<{
      technician_name: string;
      branch: string;
      department: string;
      certification_name: string;
      certification_code: string;
      days_expired: number;
    }>,
    overallCompliance: 0
  });

  // Form state
  const [formData, setFormData] = useState({
    manager_email: '',
    operations_manager_email: '',
    additional_email_1: '',
    additional_email_2: '',
    is_active: true
  });

  // Filter state
  const [selectedBranch, setSelectedBranch] = useState<string>('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchConfigs(),
        fetchLogs(),
        fetchStats()
      ]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConfigs = async () => {
    try {
      const data = await certificationNotificationApi.getConfigs();
      setConfigs(data);
    } catch (error) {
      console.error('Error fetching configs:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await certificationNotificationApi.getLogs({ limit: 50 });
      setLogs(response.logs);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const fetchStats = async () => {
    try {
      // Get compliance data by branch
      const dashboardData = await certificationNotificationApi.getDashboard();
      
      // Get expired certifications with details
      const expiredResponse = await certificationNotificationApi.getDashboard({
        days: -365, // Include all expired from past year
        include_expired: true
      });

      // Calculate branch compliance from dashboard data
      const branchCompliance = ORGANIZATION.BRANCHES.map(branch => {
        const branchCerts = dashboardData.certifications.expired
          .concat(dashboardData.certifications.critical)
          .concat(dashboardData.certifications.warning)
          .concat(dashboardData.certifications.notice)
          .filter(cert => cert.user?.branch === branch);

        const expiredInBranch = dashboardData.certifications.expired
          .filter(cert => cert.user?.branch === branch);

        const expiringSoon = dashboardData.certifications.critical
          .concat(dashboardData.certifications.warning)
          .filter(cert => cert.user?.branch === branch);

        // Calculate compliance - this is simplified, you may want to adjust logic
        const totalTechnicians = branchCerts.length;
        const compliantTechnicians = totalTechnicians - expiredInBranch.length;
        const compliance_percentage = totalTechnicians > 0 ? 
          Math.round((compliantTechnicians / totalTechnicians) * 100) : 100;

        return {
          branch,
          total_technicians: totalTechnicians,
          compliant_technicians: compliantTechnicians,
          compliance_percentage,
          expired_count: expiredInBranch.length,
          expiring_soon: expiringSoon.length
        };
      });

      // Get expired certifications with technician details
      const expiredCertifications = dashboardData.certifications.expired.map(cert => ({
        technician_name: cert.user?.name || 'Unknown',
        branch: cert.user?.branch || 'Unknown',
        department: cert.user?.department || 'Unknown',
        certification_name: cert.certification?.name || 'Unknown',
        certification_code: cert.certification?.code || 'Unknown',
        days_expired: Math.abs(cert.days_until_expiry)
      }));

      // Calculate overall compliance
      const totalTechnicians = branchCompliance.reduce((sum, branch) => sum + branch.total_technicians, 0);
      const totalCompliant = branchCompliance.reduce((sum, branch) => sum + branch.compliant_technicians, 0);
      const overallCompliance = totalTechnicians > 0 ? Math.round((totalCompliant / totalTechnicians) * 100) : 100;

      setComplianceStats({
        branchCompliance,
        expiredCertifications,
        overallCompliance
      });
    } catch (error) {
      console.error('Error fetching compliance stats:', error);
    }
  };

  const handleEdit = (config: CertificationNotificationConfig) => {
    setEditingConfig(config);
    setFormData({
      manager_email: config.manager_email || '',
      operations_manager_email: config.operations_manager_email || '',
      additional_email_1: config.additional_email_1 || '',
      additional_email_2: config.additional_email_2 || '',
      is_active: config.is_active
    });
    setShowEditModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingConfig) return;

    try {
      setLoading(true);
      
      const updateData = {
        ...formData,
        manager_email: formData.manager_email || null,
        operations_manager_email: formData.operations_manager_email || null,
        additional_email_1: formData.additional_email_1 || null,
        additional_email_2: formData.additional_email_2 || null
      };

      await certificationNotificationApi.updateConfig(editingConfig.id, updateData);
      
      setShowEditModal(false);
      setEditingConfig(null);
      await fetchConfigs();
      
      alert('Configuration updated successfully!');
    } catch (error: any) {
      console.error('Error saving config:', error);
      alert(`Failed to save configuration: ${error.response?.data?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (config: CertificationNotificationConfig) => {
    try {
      await certificationNotificationApi.updateConfig(config.id, {
        is_active: !config.is_active
      });
      await fetchConfigs();
    } catch (error) {
      console.error('Error toggling config:', error);
      alert('Failed to update configuration status');
    }
  };

  const getNotificationTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      '30_day': 'bg-blue-900 text-blue-300',
      '14_day': 'bg-yellow-900 text-yellow-300',
      '7_day': 'bg-orange-900 text-orange-300',
      'expired': 'bg-red-900 text-red-300',
      'manager_escalation': 'bg-purple-900 text-purple-300',
      'ops_escalation': 'bg-pink-900 text-pink-300'
    };
    return colors[type] || 'bg-gray-700 text-gray-300';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'failed':
        return <AlertTriangle className="w-4 h-4 text-red-400" />;
      case 'paused':
        return <div className="w-4 h-4 bg-yellow-400 rounded-full" />;
      default:
        return null;
    }
  };

  const triggerManualCheck = async () => {
    try {
      setLoading(true);
      await certificationNotificationApi.triggerDailyCheck();
      alert('Daily notification check triggered successfully');
      await fetchLogs();
    } catch (error) {
      console.error('Error triggering daily check:', error);
      alert('Failed to trigger daily check');
    } finally {
      setLoading(false);
    }
  };

  const triggerWeeklyReport = async () => {
    try {
      setLoading(true);
      await certificationNotificationApi.triggerWeeklyReport();
      alert('Weekly management report triggered successfully');
      await fetchLogs();
    } catch (error) {
      console.error('Error triggering weekly report:', error);
      alert('Failed to trigger weekly report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-gray-800 rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-white">Certification Notification Management</h2>
            <p className="text-gray-400 mt-1">Configure email notifications and monitor system activity</p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Compliance Overview */}
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Organization Compliance Overview</h3>
          
          {/* Overall Compliance Score */}
          <div className="mb-6">
            <div className="bg-gray-900 p-6 rounded-lg text-center">
              <div className="flex items-center justify-center mb-2">
                <CheckCircle className="w-8 h-8 text-green-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-400">Overall Compliance Rate</p>
                  <p className={`text-3xl font-bold ${
                    complianceStats.overallCompliance >= 90 ? 'text-green-400' :
                    complianceStats.overallCompliance >= 75 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {complianceStats.overallCompliance}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Branch Compliance Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {complianceStats.branchCompliance.map((branch) => (
              <div key={branch.branch} className="bg-gray-900 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white font-semibold">{branch.branch}</h4>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    branch.compliance_percentage >= 90 ? 'bg-green-900 text-green-300' :
                    branch.compliance_percentage >= 75 ? 'bg-yellow-900 text-yellow-300' : 'bg-red-900 text-red-300'
                  }`}>
                    {branch.compliance_percentage}%
                  </span>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-gray-400">
                    <span>Compliant:</span>
                    <span className="text-green-400">{branch.compliant_technicians}/{branch.total_technicians}</span>
                  </div>
                  {branch.expired_count > 0 && (
                    <div className="flex justify-between text-gray-400">
                      <span>Expired:</span>
                      <span className="text-red-400">{branch.expired_count}</span>
                    </div>
                  )}
                  {branch.expiring_soon > 0 && (
                    <div className="flex justify-between text-gray-400">
                      <span>Expiring Soon:</span>
                      <span className="text-orange-400">{branch.expiring_soon}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Manual Triggers */}
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Manual Actions</h3>
          <div className="flex space-x-4">
            <button
              onClick={triggerManualCheck}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              Trigger Daily Check
            </button>
            <button
              onClick={triggerWeeklyReport}
              disabled={loading}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              Send Weekly Reports
            </button>
          </div>
          <p className="text-sm text-gray-400 mt-2">
            Use these buttons to manually trigger notification checks or weekly management reports.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-700">
          <nav className="flex space-x-8 px-6">
            {[
              { key: 'configurations', label: 'Email Configurations', icon: Settings },
              { key: 'logs', label: 'Notification Logs', icon: FileText },
              { key: 'expired', label: 'Expired Certifications', icon: AlertTriangle }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === key
                    ? 'border-blue-400 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'configurations' && (
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white">Email Configurations by Branch & Department</h3>
              <p className="text-sm text-gray-400 mt-1">
                Configure email recipients for certification expiration notifications
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Branch
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Manager Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Ops Manager
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Additional
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {configs.map((config) => (
                    <tr key={config.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Building className="w-4 h-4 text-blue-400 mr-2" />
                          <span className="text-sm font-medium text-white">{config.branch}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Users className="w-4 h-4 text-purple-400 mr-2" />
                          <span className="text-sm text-white">{config.department}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {config.manager_email || (
                          <span className="text-gray-400 italic">Not configured</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {config.operations_manager_email ? (
                          <Mail className="w-4 h-4 text-blue-400" />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-700 text-gray-300">
                          {[config.additional_email_1, config.additional_email_2].filter(Boolean).length}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleActive(config)}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            config.is_active
                              ? 'bg-green-900 text-green-300'
                              : 'bg-red-900 text-red-300'
                          }`}
                        >
                          {config.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(config)}
                          className="text-blue-400 hover:text-blue-300 flex items-center"
                        >
                          <Edit3 className="w-4 h-4 mr-1" />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

        {activeTab === 'logs' && (
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white">Recent Notification Logs</h3>
              <p className="text-sm text-gray-400 mt-1">
                View recent notification activity and status
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Date/Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Technician
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Certification
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Recipients
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {logs.slice(0, 20).map((log) => (
                    <tr key={log.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {new Date(log.sent_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-white">
                            {log.technician?.name}
                          </div>
                          <div className="text-sm text-gray-400">
                            {log.technician?.branch} - {log.technician?.department}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-900 text-blue-300">
                          {log.certification?.code}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getNotificationTypeColor(log.notification_type)}`}>
                          {log.notification_type.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-700 text-gray-300">
                          {log.recipient_emails.length} recipients
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(log.status)}
                          <span className="ml-2 text-sm text-white capitalize">{log.status}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          </div>
        )}

        {activeTab === 'expired' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Expired Certifications</h3>
                <p className="text-sm text-gray-400 mt-1">
                  Technicians with expired certifications requiring immediate attention
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <label className="text-sm text-gray-400">Filter by Branch:</label>
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className="bg-gray-700 text-white rounded-lg border border-gray-600 px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Branches</option>
                  {ORGANIZATION.BRANCHES.map(branch => (
                    <option key={branch} value={branch}>{branch}</option>
                  ))}
                </select>
              </div>
            </div>

            {complianceStats.expiredCertifications.length === 0 ? (
              <div className="bg-gray-900 p-8 rounded-lg text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-white mb-2">No Expired Certifications</h4>
                <p className="text-gray-400">All technicians are compliant with their certification requirements.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Technician
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Branch/Department
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Certification
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Days Expired
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-800 divide-y divide-gray-700">
                    {complianceStats.expiredCertifications
                      .filter(cert => selectedBranch === 'all' || cert.branch === selectedBranch)
                      .map((cert, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-white">{cert.technician_name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-white">{cert.branch}</div>
                            <div className="text-sm text-gray-400">{cert.department}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900 text-red-300">
                              {cert.certification_code}
                            </span>
                            <div className="text-sm text-gray-400 mt-1">{cert.certification_name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-red-400">
                              {cert.days_expired} days
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button className="text-blue-400 hover:text-blue-300">
                              Send Reminder
                            </button>
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && editingConfig && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border border-gray-700 w-96 shadow-lg rounded-md bg-gray-800">
              <div className="mt-3">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Edit Configuration: {editingConfig.branch} - {editingConfig.department}
                </h3>
                <form onSubmit={handleSave} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Manager Email (7-day escalation)
                    </label>
                    <input
                      type="email"
                      value={formData.manager_email}
                      onChange={(e) => setFormData({ ...formData, manager_email: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="manager@mascottequipment.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Operations Manager Email (expired escalation)
                    </label>
                    <input
                      type="email"
                      value={formData.operations_manager_email}
                      onChange={(e) => setFormData({ ...formData, operations_manager_email: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="operations@mascottequipment.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Additional Email 1 (expired notifications)
                    </label>
                    <input
                      type="email"
                      value={formData.additional_email_1}
                      onChange={(e) => setFormData({ ...formData, additional_email_1: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="additional1@mascottequipment.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Additional Email 2 (expired notifications)
                    </label>
                    <input
                      type="email"
                      value={formData.additional_email_2}
                      onChange={(e) => setFormData({ ...formData, additional_email_2: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="additional2@mascottequipment.com"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 bg-gray-700 border-gray-600 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-300">
                      Active Configuration
                    </label>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditModal(false);
                        setEditingConfig(null);
                      }}
                      className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CertificationNotificationAdmin;
